import React from 'react'
import Stack from '@mui/material/Stack'
import IconButton, { IconButtonProps } from '@mui/material/IconButton'
import Button, { ButtonProps } from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Divider from '@mui/material/Divider'

export interface ActionConfig {
    id?: string
    buttonType?: 'icon' | 'text'
    icon?: React.ReactNode
    text?: string
    tooltip: string
    onClick: () => void
    disabled?: boolean
    loading?: boolean
    show?: boolean
    showDividerAfter?: boolean
    variant?: ButtonProps['variant']
    color?: ButtonProps['color']
    iconButtonProps?: Partial<IconButtonProps>
    buttonProps?: Partial<ButtonProps>
    'data-test-id'?: string
}

export interface ActionToolbarProps {
    actions: ActionConfig[]
    iconButtonSize?: 'small' | 'medium' | 'large'
}

export const ActionToolbar: React.FC<ActionToolbarProps> = ({ actions, iconButtonSize = 'medium' }) => {
    const elements: React.ReactNode[] = []

    actions.forEach((action, index) => {
        if (action.show === false) {
            return
        }

        const {
            id,
            buttonType = 'icon',
            icon,
            text,
            tooltip,
            onClick,
            disabled = false,
            loading = false,
            showDividerAfter = false,
            variant = 'outlined',
            color = 'primary',
            iconButtonProps = {},
            buttonProps = {},
            'data-test-id': testId,
        } = action

        const key = id || tooltip || index

        let buttonElement: React.ReactNode

        if (buttonType === 'text') {
            buttonElement = (
                <Tooltip key={key} title={tooltip}>
                    <span>
                        <Button
                            id={id}
                            onClick={onClick}
                            disabled={disabled || loading}
                            variant={variant}
                            color={color}
                            loading={loading}
                            data-test-id={testId}
                            sx={{ minWidth: 'auto', ...buttonProps?.sx }}
                            {...buttonProps}
                        >
                            {text}
                        </Button>
                    </span>
                </Tooltip>
            )
        } else {
            buttonElement = (
                <Tooltip key={key} title={tooltip}>
                    <span>
                        <IconButton
                            id={id}
                            onClick={onClick}
                            disabled={disabled || loading}
                            size={iconButtonSize}
                            aria-label={tooltip}
                            data-test-id={testId}
                            sx={{ padding: '6px', ...iconButtonProps?.sx }}
                            {...iconButtonProps}
                        >
                            {icon}
                        </IconButton>
                    </span>
                </Tooltip>
            )
        }

        elements.push(buttonElement)

        if (showDividerAfter) {
            elements.push(
                <Divider
                    key={`${key}-divider`}
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1, height: 24, alignSelf: 'center' }}
                />
            )
        }
    })

    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {elements}
        </Stack>
    )
}
